import {
  AdminInput,
  Disclaimer,
  Div,
  DropdownMenuComponent,
  Label,
  lightTheme,
  useOnClickOutside,
} from '@vgrubs/components';
import { useRef, useState } from 'react';
import styled from 'styled-components';
import { findSimilarNames, suggestNames } from './expenseNames';

/** One suggestion row. Same pill as the AdminCustomSelect option, repeated
 *  here because that component's styled module is not re-exported from the
 *  barrel and an app reaching into it by deep path would be worse. */
const SuggestionOption = styled.div<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 15px;
  line-height: 18px;
  padding: 8px 16px;
  border-radius: 20px;
  border: 1px solid ${({ theme }) => theme.gray};
  ${({ $active, theme }) => $active && `background-color: ${theme.gray};`}

  :hover {
    background-color: ${({ theme }) => theme.gray};
  }
`;

type Props = {
  value: string;
  onChange: (name: string) => void;
  /** Everything already on the list - drives both the autocomplete and the
   *  near-duplicate warning. */
  existingExpenses: { id: number; name: string }[];
  /** The row being edited, skipped so it never suggests or warns about itself. */
  ignoreId?: number;
  /** Closing sentence of the duplicate warning. The two forms word it
   *  differently ("Add a second line" / "Keep a second line"). */
  warningSuffix: string;
};

/**
 * The Name field for a custom expense: input, autocomplete over the names
 * already stored, and the near-duplicate warning underneath.
 *
 * The dropdown rides on the shared DropdownMenuComponent, which positions
 * itself 48px below its offsetParent. That is why the wrapper below holds ONLY
 * the input (40px tall, per the theme) and the label sits outside it - the gap
 * then lands at a clean 8px instead of somewhere over the field.
 */
export const ExpenseNameField = ({
  value,
  onChange,
  existingExpenses,
  ignoreId,
  warningSuffix,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(wrapperRef, () => setOpen(false));

  const suggestions = suggestNames(value, existingExpenses, ignoreId);
  const similarNames = value.trim()
    ? findSimilarNames(value, existingExpenses, ignoreId)
    : [];

  const dropdownOpen = open && suggestions.length > 0;
  // The list shrinks as the admin keeps typing, so a stale index is normal
  // rather than a bug; clamp it here instead of chasing it with an effect.
  const activeIndex = highlighted < suggestions.length ? highlighted : -1;

  const accept = (name: string) => {
    onChange(name);
    setOpen(false);
    setHighlighted(-1);
  };

  const handleType = (next: string) => {
    onChange(next);
    // Reopen on every keystroke, so typing again after picking a suggestion
    // brings the list back.
    setOpen(true);
    setHighlighted(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') return setOpen(false);
    if (!dropdownOpen) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      // Only swallowed while a row is highlighted, so Enter still does
      // whatever it did before for everyone typing a brand new name.
      e.preventDefault();
      accept(suggestions[activeIndex]);
    }
  };

  return (
    <div ref={wrapperRef}>
      <Label label="Name" $textCenter />

      <Div $relative>
        <AdminInput
          placeholder="Enter here"
          value={value}
          onChange={(e) => handleType(e.target.value)}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          // The browser's own history dropdown would sit on top of this one.
          autoComplete="off"
          $textCenter
        />

        <DropdownMenuComponent
          isOpen={dropdownOpen}
          fullWidth
          maxHeight="220px"
          padding="8px"
        >
          <Div $flex $column $gap="4px">
            {suggestions.map((name, i) => (
              <SuggestionOption
                key={name}
                $active={i === activeIndex}
                // Keeps focus in the input, so picking with the mouse does not
                // blur the field on the way to the click.
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setHighlighted(i)}
                onClick={() => accept(name)}
              >
                {name}
              </SuggestionOption>
            ))}
          </Div>
        </DropdownMenuComponent>
      </Div>

      {similarNames.length > 0 && (
        <Disclaimer $m="0" $mt="8" $center $color={lightTheme.darkYellow}>
          Already on the list: {similarNames.join(', ')}. {warningSuffix}
        </Disclaimer>
      )}
    </div>
  );
};
